import type { ResourceTableDescriptor, TranslateFn } from '@/components/ui/embedded-resource-table/types';
import type { ToolbarFilterOption } from '@/components/ui/generic-toolbar/GenericToolbar';
import { buildAssignedUserOptions, type AssignableUser } from '@/lib/assigned-user-options';
import { taskDaysToDue, taskScheduleTone, type TaskScheduleTone } from '@/lib/projects/task-schedule';
import { settingLabel } from '@/lib/status-labels';
import type { ProjectTask } from '@/types';

/** A task with no end date sorts last rather than jumping to the top as a zero. */

const SCHEDULE_TONES: TaskScheduleTone[] = ['overdue', 'due_today', 'due_soon', 'on_track', 'none'];

function projectId(task: ProjectTask): string {
    return task.project?.id ?? task.project_id ?? '';
}

/**
 * The one description of the project-tasks table, read by every tab that lists
 * tasks and by TaskTable itself — the same contract the leads, projects and
 * companies tables already follow, so a task list reads the same everywhere.
 */
export const taskTableDescriptor: ResourceTableDescriptor<ProjectTask> = {
    key: 'tasks',
    // Drives the `<resource>.index.toolbar.*` translation lookups; tasks have no
    // index page of their own, so they borrow the projects toolbar copy.
    resourceName: 'projects',
    resourceNamePlural: 'tasks',
    getRowId: (task) => task.id,

    columns: [
        {
            id: 'name',
            labelKey: 'projects.tables.tasks.task_name',
            labelFallback: 'Task Name',
            sort: { value: (task) => task.name ?? '' },
        },
        {
            id: 'project',
            labelKey: 'projects.tables.tasks.project',
            labelFallback: 'Project',
            sort: { value: (task) => task.project?.contract_number ?? '' },
        },
        {
            id: 'status',
            labelKey: 'projects.tables.tasks.status',
            labelFallback: 'Status',
            sort: { value: (task) => String(task.status ?? '') },
        },
        {
            id: 'start_date',
            labelKey: 'projects.tables.tasks.start_date',
            labelFallback: 'Start Date',
            sort: { value: (task) => task.start_date ?? '' },
        },
        {
            id: 'end_date',
            labelKey: 'projects.tables.tasks.end_date',
            labelFallback: 'End Date',
            sort: { value: (task) => task.end_date ?? '' },
        },
        {
            // Ordered by days-to-due rather than by the badge text, so sorting puts
            // the most overdue task first instead of grouping by wording.
            id: 'schedule',
            labelKey: 'projects.tables.tasks.schedule',
            labelFallback: 'Schedule',
            // null, not a sentinel: applyClientSort buries blanks last in *both*
            // directions, whereas MAX_SAFE_INTEGER put undated tasks first on a
            // descending sort — the opposite of the intent.
            sort: { value: (task) => taskDaysToDue(task) },
        },
        // Off by default: on an employee's profile every row holds the same face.
        // Kept in the catalogue so a shared task list can switch it back on.
        {
            id: 'assignee',
            labelKey: 'projects.tables.tasks.assignee',
            labelFallback: 'Assignee',
            compact: true,
        },
    ],

    defaultVisibleColumns: ['name', 'project', 'status', 'start_date', 'end_date', 'schedule'],

    filters: [
        {
            id: 'status',
            param: 'status',
            labelKey: 'projects.tables.tasks.status',
            labelFallback: 'Status',
            matches: (task, values) => values.includes(String(task.status ?? '')),
        },
        {
            id: 'schedule',
            param: 'schedule',
            labelKey: 'projects.tables.tasks.schedule',
            labelFallback: 'Schedule',
            matches: (task, values) => values.includes(taskScheduleTone(task)),
        },
        {
            id: 'project',
            param: 'project_id',
            labelKey: 'projects.tables.tasks.project',
            labelFallback: 'Project',
            matches: (task, values) => values.includes(projectId(task)),
        },
        {
            id: 'assignee',
            param: 'assigned_to',
            labelKey: 'projects.tables.tasks.assignee',
            labelFallback: 'Assignee',
            matches: (task, values) => values.includes(task.assigned_to ?? ''),
        },
    ],

    // Every filter is a quick filter: there are only four, and a "More filters"
    // list would just repeat them as columns.
    quickFilterIds: ['status', 'schedule', 'project', 'assignee'],
    showMoreFilters: false,

    searchValues: (task) => [task.name, task.project?.contract_number, task.project?.contract_name],

    allLabel: { labelKey: 'projects.tables.tasks.all_tasks', labelFallback: 'All tasks' },
    quickFiltersLabel: { labelKey: 'projects.index.toolbar.quick_filters', labelFallback: 'Quick filters' },
};

export interface TaskFilterOptionSources {
    t: TranslateFn;
    statuses: Array<{ value: string; label?: string }>;
    users: AssignableUser[];
    projects: Array<{ id: string; label: string }>;
}

export function taskFilterOptions({ t, statuses, users, projects }: TaskFilterOptionSources): Record<string, ToolbarFilterOption[]> {
    return {
        status: statuses.map((status) => ({
            value: status.value,
            label: settingLabel(t, 'project_task_statuses', status.value) || status.label || status.value,
        })),
        schedule: SCHEDULE_TONES.map((tone) => ({
            value: tone,
            label: t(`projects.tables.tasks.schedule_options.${tone}`, tone),
        })),
        project: projects.map((project) => ({ value: project.id, label: project.label })).sort((a, b) => a.label.localeCompare(b.label)),
        assignee: buildAssignedUserOptions(users, t),
    };
}

/** Fallback sources for a tab handed rows but no reference lists. */
export function taskOptionSourcesFromRows(tasks: ProjectTask[]): Pick<TaskFilterOptionSources, 'users' | 'projects'> {
    const users = new Map<string, AssignableUser>();
    const projects = new Map<string, { id: string; label: string }>();

    tasks.forEach((task) => {
        if (task.assignee?.id) {
            users.set(task.assignee.id, task.assignee as unknown as AssignableUser);
        }

        const id = projectId(task);
        if (id) {
            projects.set(id, {
                id,
                label: task.project?.contract_number || task.project?.contract_name || id,
            });
        }
    });

    return { users: [...users.values()], projects: [...projects.values()] };
}
